import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getShapesBounds } from '../../utils/helpers/getShapesBounds';
import { isShapeInSelection } from '../../utils/helpers/isShapeInSelection';
import { rotatePoint, rotatePoints } from '../../utils/helpers/shapeTransform';
import { getNextShapeZIndex, getPreviousShapeZIndex, normalizeShapeZIndexes } from '../../utils/helpers/shapeZIndex';
import { DEFAULT_MAP_CONFIG, MapConfig } from '../mapConfigSerialization';
import {
  PickedBoxParams,
  SelectionBox,
  Shape,
  ShapeType,
  UpdateShapeParamsPayload,
  WarehouseMapConstructorSchema,
  WarehouseMapTool,
} from '../types';

const assignChangedFields = <T extends object>(target: T, params: Partial<T>): boolean => {
  let changed = false;
  (Object.keys(params) as Array<keyof T>).forEach((key) => {
    const next = params[key];
    if (target[key] !== next) {
      target[key] = next as T[keyof T];
      changed = true;
    }
  });
  return changed;
};

const initialState: WarehouseMapConstructorSchema = {
  selectedFloorId: null,
  activeTool: WarehouseMapTool.POINTER,
  shapes: [],
  pickedBoxParams: null,
  snapStepPixels: 10,
  showGrid: true,
  mapConfig: DEFAULT_MAP_CONFIG,
  vertexEditShapeId: null,
};

const warehouseMapConstructorSlice = createSlice({
  name: 'warehouseMapConstructor',
  initialState,
  reducers: {
    changeFloor: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      state.selectedFloorId = action.payload;
    },
    changeTool: (state: WarehouseMapConstructorSchema, action: PayloadAction<WarehouseMapTool>) => {
      state.activeTool = action.payload;
    },
    setMapConfig: (state: WarehouseMapConstructorSchema, action: PayloadAction<MapConfig>) => {
      state.mapConfig = action.payload;
    },
    setImageTransform: (state: WarehouseMapConstructorSchema, action: PayloadAction<Partial<MapConfig['image']>>) => {
      Object.assign(state.mapConfig.image, action.payload);
      state.mapConfig.image.opacity = Math.min(1, Math.max(0, state.mapConfig.image.opacity));
    },
    setLabelFontSize: (state: WarehouseMapConstructorSchema, action: PayloadAction<number>) => {
      state.mapConfig.labelFontSize = Math.max(1, action.payload);
    },
    resetMapConfig: (state: WarehouseMapConstructorSchema) => {
      state.mapConfig = DEFAULT_MAP_CONFIG;
    },
    setInitialShapes: (state: WarehouseMapConstructorSchema, action: PayloadAction<Shape[]>) => {
      state.shapes = normalizeShapeZIndexes(action.payload);
    },
    appendShape: (state: WarehouseMapConstructorSchema, action: PayloadAction<Shape | Shape[]>) => {
      for (const shape of state.shapes) {
        if (shape.selected) shape.selected = false;
      }

      const nextShapes = Array.isArray(action.payload) ? action.payload : [action.payload];
      const nextZIndex = getNextShapeZIndex(state.shapes);
      nextShapes.forEach((shape, index) => {
        state.shapes.push({ ...shape, selected: true, zIndex: nextZIndex + index });
      });
    },
    // The three selection mutators below run on every pointer interaction
    // (selectShapesInArea fires per-mousemove during marquee). Mutating in
    // place lets immer keep references for shapes whose `selected` state
    // didn't actually change, and skip producing a new `state.shapes`
    // array entirely when nothing changed. Combined with the picker's
    // narrowed `getPlacedShapeIds` subscription this means an empty
    // marquee drag costs zero re-renders downstream.
    selectShape: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      const targetId = action.payload;
      for (const shape of state.shapes) {
        const next = shape.id === targetId;
        if (shape.selected !== next) shape.selected = next;
      }
    },
    addShapeToSelection: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      const shape = state.shapes.find((s) => s.id === action.payload);
      if (shape && !shape.locked && !shape.selected) shape.selected = true;
    },
    toggleShapeSelection: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      const shape = state.shapes.find((s) => s.id === action.payload);
      if (shape && !shape.locked) shape.selected = !shape.selected;
    },
    selectAllShapes: (state: WarehouseMapConstructorSchema, action: PayloadAction<{ floorId: string }>) => {
      const { floorId } = action.payload;
      for (const shape of state.shapes) {
        const next = shape.floorId === floorId && !shape.locked;
        if (shape.selected !== next) shape.selected = next;
      }
    },
    unselectShapes: (state: WarehouseMapConstructorSchema) => {
      for (const shape of state.shapes) {
        if (shape.selected) shape.selected = false;
      }
    },
    selectShapesInArea: (state: WarehouseMapConstructorSchema, action: PayloadAction<SelectionBox>) => {
      const selection = action.payload;
      for (const shape of state.shapes) {
        const next = !shape.locked && isShapeInSelection(shape, selection);
        if (shape.selected !== next) shape.selected = next;
      }
    },
    deleteShape: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      state.shapes = state.shapes.filter((shape) => shape.id !== action.payload);
    },
    toggleLockShape: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      const shapeIndex = state.shapes.findIndex((shape) => shape.id === action.payload);

      if (shapeIndex === -1) return;

      const [shape] = state.shapes.splice(shapeIndex, 1);
      shape.locked = !shape.locked;

      // Move locked shapes to the back of canvas and unlocked shapes forward.
      if (shape.locked) {
        shape.zIndex = getPreviousShapeZIndex(state.shapes);
        state.shapes.unshift(shape);
      } else {
        shape.zIndex = getNextShapeZIndex(state.shapes);
        state.shapes.push(shape);
      }
    },
    updateShapeParams: (
      state: WarehouseMapConstructorSchema,
      action: PayloadAction<{ id: string; params: Partial<UpdateShapeParamsPayload> }>,
    ) => {
      const shape = state.shapes.find((item) => item.id === action.payload.id);
      if (!shape) return;
      assignChangedFields(shape, action.payload.params as Partial<Shape>);
    },
    swapRectangleDimensions: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      const shape = state.shapes.find((item) => item.id === action.payload);
      if (!shape || shape.type !== ShapeType.RECTANGLE || shape.locked) return;

      [shape.width, shape.height] = [shape.height, shape.width];
      [shape.scaleX, shape.scaleY] = [shape.scaleY, shape.scaleX];
    },
    moveShapesByIds: (
      state: WarehouseMapConstructorSchema,
      action: PayloadAction<{ shapeIds: string[]; deltaX: number; deltaY: number; excludedShapeId: string }>,
    ) => {
      const { deltaX, deltaY, excludedShapeId } = action.payload;
      if (deltaX === 0 && deltaY === 0) return;

      const shapeIds = new Set(action.payload.shapeIds);
      for (const shape of state.shapes) {
        if (!shapeIds.has(shape.id) || shape.locked || shape.id === excludedShapeId) continue;
        if ('points' in shape) {
          shape.points = shape.points.map((point, index) => (index % 2 === 0 ? point + deltaX : point + deltaY));
          continue;
        }

        shape.x += deltaX;
        shape.y += deltaY;
      }
    },
    /**
     * Generic mutation against all currently selected shapes. The caller
     * passes a partial keyed by the field(s) it wants to update; every
     * selected shape receives the same write. `assignChangedFields` ensures
     * only truly modified fields dirty the Immer draft, so single-select
     * behavior is unchanged and no-op calls produce no new references.
     */
    updateSelectedShape: (state: WarehouseMapConstructorSchema, action: PayloadAction<Partial<Shape>>) => {
      for (const shape of state.shapes) {
        if (shape.selected) assignChangedFields(shape, action.payload);
      }
    },
    syncShapeSizeCodes: (state: WarehouseMapConstructorSchema, action: PayloadAction<Record<string, string>>) => {
      const sizeCodeByShapeId = action.payload;
      for (const shape of state.shapes) {
        if ((shape.type !== ShapeType.RECTANGLE && shape.type !== ShapeType.POLYGON_BOX) || shape.sizeCodeId) continue;
        const matchedSizeCodeId = sizeCodeByShapeId[shape.id];
        if (!matchedSizeCodeId) continue;
        shape.sizeCodeId = matchedSizeCodeId;
      }
    },
    rotateSelectedShapes: (state: WarehouseMapConstructorSchema, action: PayloadAction<{ rotation: number }>) => {
      const selectedShapes = state.shapes.filter((shape) => shape.selected && !shape.locked);
      if (!selectedShapes.length) return;

      if (selectedShapes.length === 1) {
        selectedShapes[0].rotation = action.payload.rotation;
        return;
      }

      const bounds = getShapesBounds(selectedShapes);
      if (!bounds) return;

      const sharedRotation = selectedShapes.every((shape) => (shape.rotation ?? 0) === (selectedShapes[0].rotation ?? 0))
        ? selectedShapes[0].rotation ?? 0
        : 0;
      const delta = action.payload.rotation - sharedRotation;
      if (delta === 0) return;

      const origin = { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };

      for (const shape of selectedShapes) {
        if ('points' in shape) {
          shape.points = rotatePoints(shape.points, origin, delta);
        } else {
          const nextPosition = rotatePoint({ x: shape.x, y: shape.y }, origin, delta);
          shape.x = nextPosition.x;
          shape.y = nextPosition.y;
          shape.rotation = (shape.rotation ?? 0) + delta;
        }
      }
    },
    setPickedBoxParams: (state: WarehouseMapConstructorSchema, action: PayloadAction<PickedBoxParams>) => {
      state.pickedBoxParams = action.payload;
    },
    changeSnapStepPixels: (state: WarehouseMapConstructorSchema, action: PayloadAction<number>) => {
      state.snapStepPixels = action.payload;
    },
    toggleGrid: (state: WarehouseMapConstructorSchema) => {
      state.showGrid = !state.showGrid;
    },
    deleteSelected: (state: WarehouseMapConstructorSchema) => {
      // Locked shapes are skipped to match `nudgeSelected` and
      // `selectShapesInArea`. Use the explicit `deleteShape(id)` action to
      // remove a locked shape (e.g. via the lock-aware ParametersBar UI).
      state.shapes = state.shapes.filter((shape) => !shape.selected || shape.locked);
    },
    nudgeSelected: (state: WarehouseMapConstructorSchema, action: PayloadAction<{ dx: number; dy: number }>) => {
      const { dx, dy } = action.payload;
      if (dx === 0 && dy === 0) return;

      for (const shape of state.shapes) {
        if (!shape.selected || shape.locked) continue;
        if ('points' in shape) {
          shape.points = shape.points.map((point, index) => (index % 2 === 0 ? point + dx : point + dy));
          continue;
        }

        shape.x += dx;
        shape.y += dy;
      }
    },
    startVertexEdit: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      state.vertexEditShapeId = action.payload;
      // Deselect all so the Transformer doesn't interfere with vertex handles.
      for (const shape of state.shapes) {
        if (shape.selected) shape.selected = false;
      }
    },
    endVertexEdit: (state: WarehouseMapConstructorSchema) => {
      state.vertexEditShapeId = null;
    },
    /**
     * Converts a RECTANGLE or CIRCLE shape in-place to a POLYGON_BOX,
     * preserving label, sizeCodeId, fill and stroke. Rotation is baked into
     * the absolute world-space vertex coordinates so the resulting polygon has
     * rotation = 0 and scaleX/Y = 1.
     */
    transformToPolygon: (state: WarehouseMapConstructorSchema, action: PayloadAction<string>) => {
      const index = state.shapes.findIndex((s) => s.id === action.payload);
      if (index === -1) return;

      const shape = state.shapes[index];
      let points: number[] = [];

      if (shape.type === ShapeType.RECTANGLE) {
        const { x, y } = shape;
        const w = shape.width * shape.scaleX;
        const h = shape.height * shape.scaleY;
        const rad = ((shape.rotation ?? 0) * Math.PI) / 180;
        const cosA = Math.cos(rad);
        const sinA = Math.sin(rad);
        // Bake rotation into world-space corners (rotated around the rect's top-left origin).
        points = [x, y, x + w * cosA, y + w * sinA, x + w * cosA - h * sinA, y + w * sinA + h * cosA, x - h * sinA, y + h * cosA];
      } else if (shape.type === ShapeType.CIRCLE) {
        const { x: cx, y: cy } = shape;
        const rx = shape.radiusX * shape.scaleX;
        const ry = shape.radiusY * shape.scaleY;
        const rad = ((shape.rotation ?? 0) * Math.PI) / 180;
        const cosR = Math.cos(rad);
        const sinR = Math.sin(rad);
        // Approximate ellipse with 12 evenly-spaced vertices.
        for (let i = 0; i < 12; i++) {
          const angle = (2 * Math.PI * i) / 12;
          const lx = rx * Math.cos(angle);
          const ly = ry * Math.sin(angle);
          points.push(cx + lx * cosR - ly * sinR, cy + lx * sinR + ly * cosR);
        }
      } else {
        return;
      }

      const { id, floorId, fill, stroke, strokeWidth, selected, locked, zIndex } = shape;
      const label = 'label' in shape ? shape.label : '';
      const sizeCodeId = 'sizeCodeId' in shape ? shape.sizeCodeId : undefined;

      const polygonBox: Shape = {
        type: ShapeType.POLYGON_BOX,
        id,
        floorId,
        x: 0,
        y: 0,
        scaleX: 1,
        scaleY: 1,
        rotation: 0,
        points,
        fill,
        stroke,
        strokeWidth,
        label,
        sizeCodeId,
        selected: selected ?? false,
        locked,
        zIndex,
      };
      state.shapes[index] = polygonBox;
    },
    duplicateSelectedShapes: (state: WarehouseMapConstructorSchema, action: PayloadAction<{ offsetX: number; offsetY: number }>) => {
      const { offsetX, offsetY } = action.payload;
      const selectedShapes = state.shapes.filter((shape) => shape.selected);
      if (!selectedShapes.length) return;

      const nextZIndex = getNextShapeZIndex(state.shapes);
      const copies = selectedShapes.map((shape, index) => {
        const id = crypto.randomUUID();
        // Force `locked: false` so the duplicate is immediately editable.
        const base = { ...shape, id, selected: true, locked: false, zIndex: nextZIndex + index };
        if ('points' in base) {
          return {
            ...base,
            points: base.points.map((point, index) => (index % 2 === 0 ? point + offsetX : point + offsetY)),
          };
        }
        return { ...base, x: base.x + offsetX, y: base.y + offsetY };
      });

      for (const shape of state.shapes) {
        if (shape.selected) shape.selected = false;
      }
      state.shapes.push(...copies);
    },
  },
});

export const { actions: warehouseMapConstructorActions, reducer: warehouseMapConstructorReducer } = warehouseMapConstructorSlice;
